import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { FetchProjectTags } from '@trinity/chat/api/interfaces/projects';
import { createNamedContext } from '@trinity/chat-components/utils/react';
import type { TagItem } from './interfaces';

export type ProjectTagsQueryResult = UseQueryResult<FetchProjectTags.Response>;

export const ProjectTagsQueryContext = createNamedContext<ProjectTagsQueryResult | undefined>(
  undefined as ProjectTagsQueryResult | undefined,
  'Project.Tags.Query.Context'
);

export function sortTags<T extends { ordinal: number }>(tags: T[]): T[] {
  return [...tags].sort((a, b) => (a.ordinal ?? Number.MAX_VALUE) - (b.ordinal ?? Number.MAX_VALUE));
}

export function buildProjectTagsWithChildren(tags: FetchProjectTags.Response['tags'] | undefined): TagItem[] {
  if (!tags) return [];
  return sortTags(
    tags
      .filter((t) => !t.parent?.id)
      .map((t) => ({
        ...t,
        children: sortTags(tags.filter((c) => c.parent?.id === t.id)),
      }))
  );
}

export function useProjectTagsWithChildren(tags: FetchProjectTags.Response['tags'] | undefined): TagItem[] {
  return useMemo(() => buildProjectTagsWithChildren(tags), [tags]);
}
